import {
  ComponentPropsWithRef,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import styled from 'styled-components';
import styledMap from 'styled-map';
import {Status, useImage} from '../../../hooks';
import {userInitials} from '../../../utils';
import {capitalize} from '../../../utils';
import {Typography} from '../typography/typography';
import {getColorFromTheme} from '../../ui-utils';
import {useLayer} from 'react-laag';
import {TextTag2} from '../tag';
import {motion, AnimatePresence} from 'framer-motion';
import {HorizontalSpacer, VerticalSpacer} from '../spacer';
import {Body2, CTA, Headline4} from '../typography';
import {useStoreContext} from '../../../store/store-context';
import {Groups} from '../../molecules/groups';
import {ItemLoaderLine} from '../../organisms/item-loader-line';
import {ifProp} from 'styled-tools';
import {formatTimezoneForUser} from '../../../utils/timezone';
import {
  avatarBackgroundColor,
  avatarForegroundColor,
  avatarHueFor,
} from '../../style/avatar-colors';

export type AvatarProps = ComponentPropsWithRef<'img'> & {
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xs';
  name: string;
  optional?: string;
  userId: string;
  isUserDeleted?: boolean;
  customUsers?: any[];
  avatarIcon?: ReactNode;
  active?: boolean;
  tooltip: boolean;
  timezone?: string;
  showTimezone?: boolean;
};

const size = styledMap('size', {
  default: '32px',
  sm: '24px',
  xs: '16px',
  md: '32px',
  lg: '40px',
  xl: '100px',
});

const fontSize = styledMap('size', {
  default: '10px',
  sm: '12px',
  md: '14px',
  lg: '16px',
  xl: '24px',
});

const AvatarWrapper = styled.div`
  position: relative;
`;
const DefaultWrapper = styled.div<
  Pick<AvatarProps, 'size'> &
    Pick<AvatarProps, 'active'> & {$avatarHue?: number}
>`
  width: ${size};
  height: ${size};
  border-radius: 100%;
  box-shadow: ${({active, theme}) =>
    active
      ? `0 0 0 2px ${theme.colors.purple300}`
      : `0 0 0 2px ${theme.colors.alwaysWhite}`};

  html[data-theme='dark'] & {
    box-shadow: ${({active, theme}) =>
      active ? `0 0 0 2px ${theme.colors.purple300}` : 'none'};
  }

  overflow: hidden;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  background-color: ${({$avatarHue, theme}) =>
    $avatarHue === undefined
      ? theme.colors.backgroundDark
      : avatarBackgroundColor($avatarHue, theme.avatarTone)};
  color: ${({$avatarHue, theme}) =>
    $avatarHue === undefined
      ? theme.colors.textDark
      : avatarForegroundColor($avatarHue, theme.avatarTone)};

  .tooltip {
    color: ${getColorFromTheme('textDark')};
    background: ${getColorFromTheme('white')};
    border: 1px solid ${getColorFromTheme('borderLight')};
    box-sizing: border-box;
    box-shadow: ${({theme}) => theme.elevation.level2};
    border-radius: 10px;
    padding: 16px;
  }
`;

const TooltipWrapper = styled.div`
  padding: 16px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  box-shadow: ${({theme}) => theme.elevation.level2};
  z-index: 999999999999999999999999;
  color: ${getColorFromTheme('textDark')};
  background: ${getColorFromTheme('white')};
  width: 249px;
`;

const EmptyGroups = styled.p`
  margin: 0;
  color: ${getColorFromTheme('textBody')};
  font-size: 14px;
  line-height: 20px;
`;

const AvatarImg = styled.img<Pick<AvatarProps, 'size'>>`
  width: 100%;
  height: 100%;
  overflow: hidden;
  object-fit: cover;
  object-position: center;
`;

const AvatarIcon = styled.div<{avatarIsText?: boolean}>`
  position: absolute;
  left: ${ifProp('avatarIsText', '0px', '-3px')};
  bottom: ${ifProp('avatarIsText', '-4px', '0px')};
  border: 2px solid white;
  border-radius: 100%;
  z-index: 9999;
  width: fit-content;
  height: min-content;
`;

const AvatarText = styled.span<Pick<AvatarProps, 'size'>>`
  ${Typography};
  background-color: transparent;
  color: inherit;
  font-weight: 500;
  line-height: 0;
  font-size: ${fontSize};
`;

const MotionDiv = styled(motion.div)`
  z-index: 9999999999999999999;
`;

const TooltipDivider = styled.div`
  height: 1px;
  border-top: 1px solid ${getColorFromTheme('borderLight')};
`;

interface AvatarTooltipContentProps {
  avatar: ReactNode;
  active?: boolean;
  userDetails?: any;
  name: string;
  isUserDeleted?: boolean;
  jobTitle: string;
  timezone?: string;
  showTimezone?: boolean;
  groups: any[];
  reviewer: any;
  isFetchingGroups: boolean;
  isFetchingReviewerData: boolean;
}

export const resolveAvatarGroups = (
  userDetails: any,
  availableGroups: any[] = [],
) => {
  const populatedGroups = Array.isArray(userDetails?.group)
    ? userDetails.group
    : [];
  const membershipGroups = Array.isArray(userDetails?.groups)
    ? userDetails.groups
        .map((membership: any) => {
          const groupValue = membership?.group;
          if (groupValue && typeof groupValue === 'object') return groupValue;
          return availableGroups.find((item: any) => item.id === groupValue);
        })
        .filter(Boolean)
    : [];

  const resolved = new Map<string, {id: string; name: string; value: string}>();
  [...populatedGroups, ...membershipGroups].forEach((item: any) => {
    const name = item?.name;
    const value = item?.id || item?.value || name;
    if (name && value) {
      const id = String(value);
      resolved.set(id, {id, name, value: id});
    }
  });

  return Array.from(resolved.values());
};

const AvatarTooltipContent = ({
  avatar,
  active,
  userDetails,
  name,
  isUserDeleted,
  jobTitle,
  timezone,
  showTimezone,
  groups,
  reviewer,
  isFetchingGroups,
  isFetchingReviewerData,
}: AvatarTooltipContentProps) => (
  <TooltipWrapper role="tooltip" data-testid="avatar-profile-card">
    <div style={{display: 'flex', alignItems: 'center'}}>
      {avatar}
      <HorizontalSpacer size="10px" />
      <div style={{minWidth: 0}}>
        <div style={{display: 'flex', marginBottom: '4px'}}>
          {userDetails?.role === 'admin' && (
            <TextTag2>{capitalize(userDetails.role)}</TextTag2>
          )}
          {active && (
            <TextTag2
              style={{
                marginLeft: userDetails?.role === 'admin' ? '6px' : '',
              }}>
              Goal owner
            </TextTag2>
          )}
        </div>
        <Headline4 kind="textDark" style={{wordBreak: 'break-word'}}>
          {name} {isUserDeleted ? '(Deleted user)' : ''}
        </Headline4>
        <Body2 kind="textBody">
          {isFetchingReviewerData ? 'Loading' : jobTitle || '--'}
        </Body2>
      </div>
    </div>

    <VerticalSpacer size="16px" />
    <div>
      <CTA kind="textDark" weight="regular">
        Group(s)
      </CTA>
      <VerticalSpacer size="6px" />
      {isFetchingGroups ? (
        <ItemLoaderLine />
      ) : groups.length === 0 ? (
        <EmptyGroups>No groups</EmptyGroups>
      ) : (
        <Groups items={groups} total={groups.length} groupDisplayLength={1} />
      )}
    </div>

    {showTimezone && (
      <>
        <VerticalSpacer size="16px" />
        <TooltipDivider />
        <VerticalSpacer size="16px" />
        <div>
          <CTA kind="textDark" weight="regular">
            Timezone
          </CTA>
          <VerticalSpacer size="6px" />
          <Body2 kind="textBody">{timezone || '—'}</Body2>
        </div>
      </>
    )}

    {reviewer.name && (
      <>
        <VerticalSpacer size="16px" />
        <TooltipDivider data-testid="avatar-manager-divider" />
        <VerticalSpacer size="16px" />
        <div>
          <CTA kind="textDark">Manager</CTA>
          <VerticalSpacer size="6px" />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}>
            <Body2
              kind="textBody"
              style={{
                overflowWrap: 'break-word',
                wordWrap: 'break-word',
                msWordBreak: 'break-all',
                wordBreak: 'break-word',
              }}>
              {reviewer.name}
            </Body2>
            <DefaultWrapper
              size="sm"
              $avatarHue={avatarHueFor(reviewer.id, reviewer.name)}>
              {reviewer.avatar === '' ? (
                <AvatarText size="sm">{userInitials(reviewer.name)}</AvatarText>
              ) : (
                <AvatarImg
                  src={reviewer.avatar}
                  size="sm"
                  alt={reviewer.name}
                />
              )}
            </DefaultWrapper>
          </div>
        </div>
      </>
    )}
  </TooltipWrapper>
);

export const Avatar = memo(function ({
  name,
  size,
  userId,
  active,
  tooltip,
  isUserDeleted,
  customUsers,
  avatarIcon,
  optional,
  timezone,
  showTimezone = true,
  ...props
}: AvatarProps) {
  const [status] = useImage(props.src || '');
  const [isOpen, setOpen] = useState(false);

  // helper function to close the menu
  function close() {
    setOpen(false);
  }
  const {
    usersStore: {users},
    groupStore: {groups: group},
  } = useStoreContext();
  const {renderLayer, triggerProps, layerProps} = useLayer({
    isOpen,
    onOutsideClick: close, // close the menu when the user clicks outside
    onDisappear: close, // close the menu when the menu gets scrolled out of sight
    overflowContainer: true, // keep the menu positioned inside the container
    auto: true, // automatically find the best placement
    placement: 'top-end', // we prefer to place the menu "top-end"
    triggerOffset: 12, // keep some distance to the trigger
    containerOffset: 16, // give the menu some room to breath relative to the container
    arrowOffset: 16, // let the arrow have some room to breath also
  });
  const [groups, setGroups] = useState([]);
  const [reviewer, setReviewer] = useState<any>({});
  const [jobTitle, setJobTitle] = useState<string>('');
  const [isFetchingGroups, setIsFetchingGroups] = useState(false);
  const [isFetchingReviewerData, setIsFetchingReviewerData] = useState(false);
  const userDetails: any = users?.find((user: any) => user?.id === userId);
  const resolvedTimezone = formatTimezoneForUser(
    timezone || userDetails?.timezone,
  );

  const fetchGroup = useCallback(async () => {
    setIsFetchingGroups(true);
    setIsFetchingReviewerData(true);
    const getUsers = customUsers || users;
    const userDetails: any = getUsers?.find((user: any) => user?.id === userId);

    const reviewerResponses: any = userDetails?.reviewer;

    setIsFetchingGroups(false);

    setGroups(resolveAvatarGroups(userDetails, group) as any);
    setJobTitle(userDetails?.jobTitle);

    if (reviewerResponses) {
      const reviewerResponse = reviewerResponses;
      reviewerResponse && setIsFetchingReviewerData(false);

      reviewerResponse &&
        reviewerResponse.email &&
        setReviewer({
          id: reviewerResponse.id || reviewerResponse._id,
          name:
            reviewerResponse &&
            reviewerResponse.firstName &&
            reviewerResponse.lastName
              ? `${reviewerResponse.firstName} ${reviewerResponse.lastName}`
              : reviewerResponse.email,
          avatar:
            reviewerResponse &&
            reviewerResponse.avatar &&
            reviewerResponse.avatar.url
              ? reviewerResponse.avatar.url
              : '',
        });
    }
    setIsFetchingReviewerData(false);
  }, [users, userId, group, customUsers]);

  useEffect(() => {
    if (
      !!userId &&
      userId !== 'members' &&
      userId !== 'undefined' &&
      userId !== 'managers'
    ) {
      fetchGroup();
    }
  }, [fetchGroup, userId]);

  const AvatarName = isUserDeleted ? 'Deleted user' : optional || name;
  const avatarHue = isUserDeleted
    ? undefined
    : avatarHueFor(userId, AvatarName);
  if (status !== Status.LOADED || !props.src) {
    return (
      <AvatarWrapper>
        <DefaultWrapper
          role="img"
          aria-label={`${AvatarName} avatar`}
          active={active}
          size={size}
          $avatarHue={avatarHue}
          {...triggerProps}
          onMouseEnter={() => setOpen(tooltip && true)}
          onMouseLeave={() => setOpen(false)}>
          <AvatarText size={size}>{userInitials(AvatarName)}</AvatarText>

          {renderLayer(
            <AnimatePresence>
              {isOpen && (
                <div style={{zIndex: 99999999999}}>
                  <MotionDiv
                    {...layerProps}
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}>
                    <AvatarTooltipContent
                      avatar={
                        <DefaultWrapper
                          size="lg"
                          active={active}
                          $avatarHue={avatarHue}>
                          <AvatarText size="lg">
                            {userInitials(AvatarName)}
                          </AvatarText>
                        </DefaultWrapper>
                      }
                      active={active}
                      userDetails={userDetails}
                      name={name}
                      isUserDeleted={isUserDeleted}
                      jobTitle={jobTitle}
                      timezone={resolvedTimezone}
                      showTimezone={showTimezone}
                      groups={groups}
                      reviewer={reviewer}
                      isFetchingGroups={isFetchingGroups}
                      isFetchingReviewerData={isFetchingReviewerData}
                    />
                  </MotionDiv>
                </div>
              )}
            </AnimatePresence>,
          )}
        </DefaultWrapper>
        {avatarIcon && <AvatarIcon avatarIsText>{avatarIcon}</AvatarIcon>}
      </AvatarWrapper>
    );
  }

  return (
    <AvatarWrapper>
      <DefaultWrapper
        size={size}
        active={active}
        {...triggerProps}
        onMouseEnter={() => setOpen(tooltip && true)}
        onMouseLeave={() => setOpen(false)}>
        <AvatarImg {...props} size={size} />
        {avatarIcon && <AvatarIcon>{avatarIcon}</AvatarIcon>}

        {renderLayer(
          <AnimatePresence>
            {isOpen && (
              <div style={{zIndex: 99999999999}}>
                <MotionDiv
                  {...layerProps}
                  initial={{opacity: 0}}
                  animate={{opacity: 1}}
                  exit={{opacity: 0}}>
                  <AvatarTooltipContent
                    avatar={
                      <DefaultWrapper size="lg" active={active}>
                        <AvatarImg {...props} size="lg" />
                      </DefaultWrapper>
                    }
                    active={active}
                    userDetails={userDetails}
                    name={name}
                    isUserDeleted={isUserDeleted}
                    jobTitle={jobTitle}
                    timezone={resolvedTimezone}
                    showTimezone={showTimezone}
                    groups={groups}
                    reviewer={reviewer}
                    isFetchingGroups={isFetchingGroups}
                    isFetchingReviewerData={isFetchingReviewerData}
                  />
                </MotionDiv>
              </div>
            )}
          </AnimatePresence>,
        )}
      </DefaultWrapper>
      {avatarIcon && <AvatarIcon>{avatarIcon}</AvatarIcon>}
    </AvatarWrapper>
  );
});
Avatar.displayName = 'Avatar';
